import React from 'react';

import { Add as AddIcon } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';

import { validateWithYupSchema } from '@hrlogics/react-form-utils/yup';

import {
  PeoPayrollProvidersListDocument,
  useSendInvitesToPeoPayrollProviderByCvStaffMutation,
} from 'src/api';
import { Button, FieldArrayTemplate } from 'src/components/common';
import { CheckboxField, Field, Form, TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';

import { INITIAL_VALUES, PEO_PAYROLL_PROVIDER_INITIAL_VALUES } from './constants';
import { InviteFormFields, InviteFormPeoPayrollProviderFields, InviteFormValues } from './types';
import { inviteFormValidationSchema } from './utils';

interface InvitePeoPayrollProviderByCVStaffModalFormProps {
  closeModal: () => void;
  switchClosability: (isClosureAllowed: boolean) => void;
}

export const InvitePeoPayrollProviderByCVStaffModalForm: React.FC<
  InvitePeoPayrollProviderByCVStaffModalFormProps
> = ({ closeModal, switchClosability }) => {
  const { createApolloContext } = useNotification();

  const [sendInvites] = useSendInvitesToPeoPayrollProviderByCvStaffMutation({
    context: createApolloContext({
      errorMessage: 'Failed to invite PEO Payroll Providers',
      successMessage: 'PEO Payroll Providers successfully invited',
    }),
    refetchQueries: [PeoPayrollProvidersListDocument],
  });

  const onSubmit = React.useCallback(
    async (formValues: InviteFormValues) => {
      switchClosability(false);

      try {
        await sendInvites({
          variables: formValues,
        });

        switchClosability(true);
        closeModal();
      } catch (error) {
        console.error(error);
        switchClosability(true);
      }
    },
    [closeModal, sendInvites, switchClosability],
  );

  return (
    <Box>
      <Typography variant="body1" fontWeight="bold">
        Invite PEO Payroll Providers by using their email address
      </Typography>

      <Form<InviteFormValues>
        onSubmit={onSubmit}
        subscription={{ valid: true, submitting: true }}
        containerProps={{ mt: 4 }}
        initialValues={INITIAL_VALUES}
        validate={validateWithYupSchema(inviteFormValidationSchema)}
      >
        {({ submitting, valid }) => {
          return (
            <React.Fragment>
              <FieldArrayTemplate
                label="Invitation"
                name={InviteFormFields.peoPayrollProviders}
                addIcon={<AddIcon />}
                onAdd={fields => fields.push(PEO_PAYROLL_PROVIDER_INITIAL_VALUES)}
                onRemove={(fields, index) => fields.remove(index)}
                containerProps={{ sx: { mt: 0 } }}
              >
                {({ renderFieldName }) => (
                  <Box display="flex" flexDirection="column" gap={1}>
                    <Field
                      name={renderFieldName(InviteFormPeoPayrollProviderFields.email)}
                      label="Email Address"
                      placeholder="Type Email Address"
                      required
                      component={TextField}
                      fullWidth
                      disabled={submitting}
                    />

                    <Field
                      name={renderFieldName(
                        InviteFormPeoPayrollProviderFields.isRevenueShareEnabled,
                      )}
                      label="Enable Revenue Share for this PEO Payroll Provider"
                      component={CheckboxField}
                      fullWidth
                      disabled={submitting}
                    />
                  </Box>
                )}
              </FieldArrayTemplate>

              <Box display="flex" gap={2} mt={4} justifyContent="center">
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => closeModal()}
                  disabled={submitting}
                >
                  Close
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={!valid || submitting}
                  loading={submitting}
                >
                  Send invite
                </Button>
              </Box>
            </React.Fragment>
          );
        }}
      </Form>
    </Box>
  );
};
